import { Controller } from "@hotwired/stimulus"
import { validateEmail, debounce } from "../util"

// Connects to data-controller="invite-users"
export default class extends Controller {
  static targets = [
    "option",
    "optionContent",
    "cancelButton",
    "sendButton",
    "emailInput",
    "emailInputError",
    "ambassadorTypeRadios",
    "uploadedEmailsInput"
  ]

  static values = { templates: Object }

  TYPES = {
    send_myself: "send_myself",
    send_via_tap: "send_via_tap"
  }

  isFileUploaded = false

  initialize() {
    this.handleEmailsUploaded = this.handleEmailsUploaded.bind(this)
    this.handleClearFileContent = this.handleClearFileContent.bind(this)
  }

  connect() {
    document.addEventListener("parse_emails_file:emails_uploaded", this.handleEmailsUploaded)
    document.addEventListener("parse_emails_file:clear_file_content", this.handleClearFileContent)

    this.debouncedValidateEmails = debounce(this.validateEmails.bind(this), 500)
    this.isFileUploaded = this.hasUploadedEmailsInputTarget && this.uploadedEmailsInputTarget.value.length > 0

    if (this.getSelectedOption()) {
      this.updateButtonsVisibility()
      this.toggleAmbassadorTypeRadios()

      if (this.hasEmailInputTarget && this.hasEmailInputErrorTarget) {
        this.validateEmails()
      }
    }
  }

  disconnect() {
    document.removeEventListener("parse_emails_file:emails_uploaded", this.handleEmailsUploaded)
    document.removeEventListener("parse_emails_file:clear_file_content", this.handleClearFileContent)
  }

  handleEmailsUploaded() {
    this.isFileUploaded = true
    this.validateEmails()
  }

  handleClearFileContent() {
    this.isFileUploaded = false
    this.validateEmails()
  }

  selectOption() {
    this.toggleAmbassadorTypeRadios()
    this.updateButtonsVisibility()
    this.clearSendViaTap()

    const event = new CustomEvent("invite_users:invite_option_changed", { bubbles: true })
    document.dispatchEvent(event)

    this.updateSendButtonDisabledState(true)
  }

  changeAmbassadorType(event) {
    const templateType = event.target.value
    this.syncAmbassadorTypeRadios(templateType)

    const html = this.templatesValue[templateType]
    if (!html) return

    this.updateAllEditors(html)
  }

  toggleAmbassadorTypeRadios() {
    if (!this.hasAmbassadorTypeRadiosTarget) return

    const hasSelectedOption = this.getSelectedOption() !== undefined
    this.ambassadorTypeRadiosTarget.classList.toggle("hidden", !hasSelectedOption)
  }

  syncAmbassadorTypeRadios(templateType) {
    this.ambassadorTypeRadiosTarget.querySelectorAll('input[name="invitation[ambassador_type]"]').forEach(radio => {
      radio.checked = radio.value === templateType
    })
  }

  updateAllEditors(html) {
    document.dispatchEvent(
      new CustomEvent("text-editor:set-content", { detail: { html } })
    )
  }

  updateButtonsVisibility() {
    const isSendMyself = this.getSelectedOptionType() === this.TYPES.send_myself
    this.sendButtonTarget.classList.toggle("hidden!", isSendMyself)
  }

  getSelectedOptionType() {
    const selectedOption = this.getSelectedOption()
    return selectedOption?.querySelector('input[type="radio"]')?.value
  }

  getSelectedOption() {
    return this.optionTargets.find(option => option.querySelector('input[type="radio"]').checked)
  }

  clearSendViaTap() {
    const input = this.optionContentTargets[1]?.querySelector('input[type="text"]')
    input.value = ""
  }

  validateEmailsOnInput() {
    this.debouncedValidateEmails()
  }

  validateEmails() {
    const emails = this.emailInputTarget.value
      .split(",")
      .map(email => email.trim())
      .filter(Boolean)

    const hasEmails = emails.length > 0
    const isValid = emails.every(validateEmail)

    this.emailInputErrorTarget.classList.toggle("hidden", !hasEmails || isValid)
    this.emailInputErrorTarget.textContent = isValid ? "" : "Invalid email format"

    const disabled = this.isFileUploaded
      ? hasEmails && !isValid
      : !hasEmails || !isValid

    this.updateSendButtonDisabledState(disabled)
  }

  updateSendButtonDisabledState(disabled) {
    this.sendButtonTarget.disabled = disabled
  }
}
